/**
 * EFFLEURA, 19/10/2026. « Annuler » apres la suppression d'un article du panier.
 *
 * Avant : la corbeille retirait toute la ligne (quantite comprise) d'un seul
 * clic, sans retour possible.
 *
 * Fonctionnement : on ecoute les clics sur la corbeille (.cart-items__remove)
 * en phase de capture, donc AVANT que le composant du theme ne retire la
 * ligne. On note la variante, la quantite et les proprietes de la ligne
 * (attributs data-ef-* poses par cart-products.liquid), puis on affiche un
 * bandeau « … retiré du panier. Annuler » pendant 7 secondes.
 *
 * Le bandeau est un popover « manual » pose dans le body : il passe dans la
 * couche superieure du navigateur, donc au-dessus du tiroir meme quand
 * celui-ci s'ouvre en dialogue modal (mobile), et il n'est jamais touche par
 * le rafraichissement du tiroir (morph de la section panier).
 *
 * « Annuler » rajoute la ligne via /cart/add.js en demandant les sections du
 * panier, puis emet le meme CartLinesUpdateEvent que le formulaire produit :
 * le tiroir, la page panier et la pastille de l'en-tete se mettent a jour
 * avec leur propre code, comme apres un ajout normal.
 *
 * Aucune modification du composant du theme (component-cart-items.js).
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const DUREE = 7000;
let minuterie = 0;

/** @returns {HTMLElement} */
function bandeau() {
  let el = document.getElementById('ef-annuler-retrait');
  if (el) return el;

  el = document.createElement('div');
  el.id = 'ef-annuler-retrait';
  el.className = 'ef-annuler';
  el.setAttribute('role', 'status');
  el.setAttribute('aria-live', 'polite');
  el.setAttribute('popover', 'manual');
  el.innerHTML =
    '<span class="ef-annuler__texte"></span>' +
    '<button type="button" class="ef-annuler__bouton">Annuler</button>';
  document.body.appendChild(el);
  return el;
}

/** @param {HTMLElement} el */
function montrer(el) {
  try {
    if (el.matches(':popover-open')) el.hidePopover();
    el.showPopover();
  } catch (_) {
    // Navigateur sans popover : le bandeau reste un element fixe du body.
    el.hidden = false;
  }
}

function masquer() {
  window.clearTimeout(minuterie);
  const el = document.getElementById('ef-annuler-retrait');
  if (!el) return;
  try {
    if (el.matches(':popover-open')) el.hidePopover();
  } catch (_) {
    el.hidden = true;
  }
}

/** @param {{id: string, quantity: number, properties: Record<string, string> | null, title: string}} ligne */
function proposer(ligne) {
  const el = bandeau();
  const texte = /** @type {HTMLElement} */ (el.querySelector('.ef-annuler__texte'));
  const bouton = /** @type {HTMLButtonElement} */ (el.querySelector('.ef-annuler__bouton'));

  texte.textContent = `« ${ligne.title} » retiré du panier.`;
  bouton.hidden = false;
  bouton.disabled = false;
  bouton.onclick = () => restaurer(ligne, bouton);

  montrer(el);
  window.clearTimeout(minuterie);
  minuterie = window.setTimeout(masquer, DUREE);
}

/**
 * @param {{id: string, quantity: number, properties: Record<string, string> | null, title: string}} ligne
 * @param {HTMLButtonElement} bouton
 */
async function restaurer(ligne, bouton) {
  bouton.disabled = true;
  window.clearTimeout(minuterie);

  const sections = Array.from(document.querySelectorAll('cart-items-component'))
    .map((c) => (c instanceof HTMLElement ? c.dataset.sectionId : ''))
    .filter(Boolean)
    .join(',');

  /** @type {{id: number, quantity: number, properties?: Record<string, string>}} */
  const article = { id: Number(ligne.id), quantity: ligne.quantity };
  if (ligne.properties) article.properties = ligne.properties;

  const differe = CartLinesUpdateEvent.createPromise();
  bandeau().dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: ligne.id, quantity: ligne.quantity }],
      promise: differe.promise,
    })
  );

  try {
    const routes = window.Theme?.routes || {};
    const reponse = await fetch(routes.cart_add_url || '/cart/add', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [article],
        sections,
        sections_url: window.location.pathname,
      }),
    }).then((r) => r.json());

    if (reponse.status) throw new Error(reponse.description || reponse.message || 'Ajout impossible');

    const panier = await fetch(`${routes.cart_url || '/cart'}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((r) => r.json());

    differe.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(panier),
      detail: {
        items: panier.items,
        itemCount: panier.item_count,
        sections: reponse.sections,
        source: 'effleura-annuler-retrait',
        didError: false,
      },
    });
    masquer();
  } catch (erreur) {
    differe.reject(erreur);
    const el = bandeau();
    const texte = el.querySelector('.ef-annuler__texte');
    if (texte) texte.textContent = "L'article n'a pas pu être remis dans le panier. Ajoute-le depuis sa fiche.";
    bouton.hidden = true;
    montrer(el);
    minuterie = window.setTimeout(masquer, DUREE);
  }
}

/**
 * Proprietes de ligne : Liquid serialise une ligne sans propriete en « [] »,
 * que /cart/add refuse (400). On ne garde qu'un objet non vide.
 * @param {string | undefined} brut
 * @returns {Record<string, string> | null}
 */
function lireProprietes(brut) {
  try {
    const valeur = JSON.parse(brut || 'null');
    if (valeur && typeof valeur === 'object' && !Array.isArray(valeur) && Object.keys(valeur).length > 0) {
      return valeur;
    }
  } catch (_) {
    // ignore
  }
  return null;
}

document.addEventListener(
  'click',
  (evenement) => {
    const cible = evenement.target instanceof Element ? evenement.target.closest('.cart-items__remove') : null;
    if (!cible) return;

    const rangee = cible.closest('[data-ef-variant]');
    if (!(rangee instanceof HTMLElement)) return;

    const ligne = {
      id: rangee.dataset.efVariant || '',
      quantity: Number(rangee.dataset.efQuantity) || 1,
      properties: lireProprietes(rangee.dataset.efProperties),
      title: rangee.dataset.efTitle || 'Article',
    };
    if (!ligne.id) return;

    // Laisse le theme retirer la ligne, puis propose l'annulation.
    window.setTimeout(() => proposer(ligne), 400);
  },
  true
);
